import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

// @mui
import {
  Box,
  Checkbox,
  TableRow,
  TableCell,
  TableHead,
  Typography,
  TableSortLabel,
} from '@mui/material';

import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import { nonSortableHeaders } from './nonSortableHeaders';
// ----------------------------------------------------------------------

const visuallyHidden = {
  border: 0,
  margin: -1,
  padding: 0,
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  position: 'absolute',
  whiteSpace: 'nowrap',
  clip: 'rect(0 0 0 0)',
};

TableHeaders.propTypes = {
  order: PropTypes.oneOf(['asc', 'desc']),
  orderBy: PropTypes.string,
  rowCount: PropTypes.number,
  headLabel: PropTypes.array,
  numSelected: PropTypes.number,
  onRequestSort: PropTypes.func,
  onSelectAllClick: PropTypes.func,
  indeterminate: PropTypes.bool,
  showCheckBox: PropTypes.bool,
};

export default function TableHeaders({
  order,
  orderBy,
  rowCount,
  headLabel,
  numSelected,
  onRequestSort,
  onSelectAllClick,
  indeterminate,
  showCheckBox,
}) {
  const { t } = useTranslation();
  const { isSmallScreen } = useResponsiveScreen();
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };
  return (
    <TableHead>
      <TableRow>
        {showCheckBox && (
          <TableCell padding="checkbox">
            <Checkbox
              indeterminate={numSelected > 0 && numSelected < rowCount && indeterminate}
              checked={rowCount > 0 && numSelected > 0 && numSelected === rowCount}
              onChange={onSelectAllClick}
              sx={{
                '.MuiSvgIcon-root': {
                  width: isSmallScreen ? 18 : 24,
                  height: isSmallScreen ? 18 : 24,
                },
              }}
            />
          </TableCell>
        )}
        {headLabel.map((headCell) => (
          <TableCell
            sx={
              headCell.sticky && {
                position: 'sticky',
                zIndex: '100',
                right: 0,
              }
            }
            key={headCell.id}
            align={
              headCell.alignRight ? 'right' : headCell.alignCenter ? 'center' : 'left'
            }
            sortDirection={orderBy === headCell.id ? order : false}
          >
            {nonSortableHeaders.includes(headCell.id) ? (
              // Render title without sorting for "options"
              <Typography
                variant="subtitle2"
                sx={{
                  fontSize: isSmallScreen && '0.65rem',
                }}
              >
                {t(`tableHeads.${headCell.label}`)}
              </Typography>
            ) : (
              <TableSortLabel
                hideSortIcon
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id)}
                sx={{
                  '& .MuiTableSortLabel-icon': {
                    fontSize: isSmallScreen ? '0.8rem' : undefined,
                  },
                  ml: headCell.id === 'givenName' ? (isSmallScreen ? 6 : 8) : undefined,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontSize: isSmallScreen && '0.65rem',
                  }}
                >
                  {t(`tableHeads.${headCell.label}`)}
                </Typography>
                {orderBy === headCell.id ? (
                  <Box sx={{ ...visuallyHidden }}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}
